import { z } from "zod";
import type { Operation } from "../types";

// The response contract's `current_step` enum. It validates the GET
// /api/onboarding response via createRoute, so it MUST stay permissive of the
// legacy wizard ids even though that wizard is gone (Wave D). The installed base
// persists legacy ids, and every fresh row is born on `'info'` unless
// overridden — the frozen `onboarding_state` DDL default, which the additive-
// only migration rail can never change (see steps.ts's permanence invariant).
// Removing a legacy id here would make the GET response fail validation → 500
// for any row still carrying it. So these are PERMANENTLY grandfathered (incl.
// the retired `'products'` id); only the five v2 station ids drive the live arc.
export const onboardingStep = z.enum([
  // legacy wizard ids — grandfathered forever (persisted in fleet data), no
  // longer routed to (the 15-step wizard was deleted in Wave D).
  "info",
  "use-case",
  "intent",
  "agent-cli",
  "business",
  "guide",
  "naming",
  "positioning",
  "website",
  "workspace",
  "team",
  "products",
  "connect",
  "monitor",
  "telemetry",
  // onboarding-v2 five-station arc ids (ONBOARDING-V2-PRD §8) — the live flow.
  "scan",
  "link",
  "value",
  "identity",
  "plan",
  "done",
]);

export const onboardingStateSchema = z.object({
  external_id: z.string(),
  current_step: onboardingStep,
  completed_at: z.string().nullable(),
  dismissed_at: z.string().nullable(),
  display_name: z.string().nullable(),
  use_case: z.string().nullable(),
  intent: z.string().nullable(),
  workspace_id: z.number().nullable(),
  workspace_tagline: z.string().nullable(),
  team_size: z.string().nullable(),
  installed_products: z.array(z.string()),
  company_domain: z.string().nullable(),
  telemetry_opt_in: z.boolean(),
  demo_data_seeded_at: z.string().nullable(),
  demo_data_cleared_at: z.string().nullable(),
  checklist_dismissed_at: z.string().nullable(),
  checklist_item_dismissed: z.array(z.string()),
  created_at: z.string(),
  updated_at: z.string(),
});
export type OnboardingStateView = z.infer<typeof onboardingStateSchema>;

const getResponse = z.object({ state: onboardingStateSchema });
type GetResponse = z.infer<typeof getResponse>;
const envName = z.enum(["demo", "real"]);
const envQuery = z.object({ env: envName.optional() });
type EnvQuery = z.infer<typeof envQuery>;

const getOp: Operation<void, EnvQuery, void, GetResponse> = {
  method: "GET",
  path: () => "/api/onboarding",
  query: envQuery,
  response: getResponse,
};

export const onboarding = {
  get: getOp,
};
